import { seedingPublicApi } from '@/lib/seedingPublicApi'
/**
 * OAuth 콜백 페이지 (통합)
 *
 * Cafe24 OAuth 인증 후 리다이렉트되는 통합 페이지.
 *
 * 두 가지 모드:
 * 1. 팝업 모드 (window.opener 존재): postMessage로 opener에 토큰 전달 후 자동 닫기
 * 2. 리다이렉트 모드 (팝업 차단): return_url로 리다이렉트하며 토큰을 URL hash로 전달
 *
 * 크로스 도메인 이슈:
 * - apply 페이지(seeding 도메인)와 callback 페이지(app 도메인)가 다른 도메인
 * - localStorage/StorageEvent는 같은 도메인에서만 동작
 * - 따라서 postMessage 또는 URL 파라미터로 토큰 전달
 */
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/apply/callback')({
  component: OAuthCallbackPage,
})

function OAuthCallbackPage() {
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(true)
  const [isSuccess, setIsSuccess] = useState(false)

  useEffect(() => {
    const processCallback = async () => {
      try {
        // URL에서 code와 state 추출
        const params = new URLSearchParams(window.location.search)
        const code = params.get('code')
        const state = params.get('state')
        const errorParam = params.get('error')

        // 에러 확인
        if (errorParam) {
          throw new Error(`OAuth 에러: ${errorParam}`)
        }

        if (!code || !state) {
          throw new Error('인증 코드가 없어요.')
        }

        // State 검증은 백엔드에서 처리 (state에 mall_id/shop_no/csrf 포함)

        // OAuth 콜백 처리 (토큰 교환)
        const result = await seedingPublicApi.handleAuthCallback(code, state)

        if (window.opener && !window.opener.closed) {
          // 팝업 모드: postMessage로 opener에 토큰/prefill 전달
          window.opener.postMessage({
            type: 'oauth_callback_success',
            session_token: result.session_token,
            prefill: result.prefill || null,
          }, '*')

          // 성공 UI 표시 후 자동 닫기
          setIsProcessing(false)
          setIsSuccess(true)
          setTimeout(() => window.close(), 1500)
        } else if (result.return_url) {
          // 리다이렉트 모드 (팝업 차단): return_url로 리다이렉트
          // 토큰과 prefill을 URL hash로 전달 (서버에 전송되지 않도록)
          const hashData = {
            session_token: result.session_token,
            prefill: result.prefill || null,
          }
          const hash = encodeURIComponent(JSON.stringify(hashData))
          window.location.href = `${result.return_url}#oauth=${hash}`
        } else {
          // 둘 다 안 되는 경우 (팝업 차단 + return_url 없음)
          setIsProcessing(false)
          setIsSuccess(true)
        }
      } catch (err: any) {
        console.error('OAuth callback error:', err)
        setError(err.message || '로그인 처리 중 오류가 발생했어요.')
        setIsProcessing(false)
      }
    }

    processCallback()
  }, [])

  // 에러 상태
  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-surface-lowest rounded-lg shadow-lg p-8 text-center">
            <span className="text-4xl mb-4 block">⚠️</span>
            <h1 className="text-xl font-bold text-on-surface mb-2">
              로그인 실패
            </h1>
            <p className="text-on-surface-variant mb-6">{error}</p>
            <button
              onClick={() => window.close()}
              className="px-6 py-2 bg-on-surface-variant text-white rounded-lg hover:bg-on-surface"
            >
              창 닫기
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 성공 상태 - 창 닫기 안내
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-surface-lowest rounded-lg shadow-lg p-8 text-center">
            <span className="text-4xl mb-4 block">✅</span>
            <h1 className="text-xl font-bold text-on-surface mb-2">
              로그인 완료!
            </h1>
            <p className="text-on-surface-variant mb-6">
              이 창을 닫고 신청 페이지로 돌아가주세요.
            </p>
            <button
              onClick={() => window.close()}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dim"
            >
              창 닫기
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 처리 중
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="max-w-md mx-auto px-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-on-surface-variant">로그인 처리 중...</p>
      </div>
    </div>
  )
}
